import React, { useState, useEffect, useCallback } from 'react';
import { 
  Building2, Users, Search, Plus, MoreVertical, Eye, Edit, 
  Pause, Play, DollarSign, Filter, FileText, CheckCircle, XCircle,
  ChevronLeft, ChevronRight, Loader2, Clock, Globe, Mail, Phone,
  AlertCircle, Check, Percent, Briefcase, LogIn
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription 
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import Sidebar from '../components/Sidebar';
import api from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

// Default revenue tiers
const DEFAULT_REVENUE_TIERS = [
  { min: 0, max: 5000, percentage: 50 },
  { min: 5001, max: 20000, percentage: 55 },
  { min: 20001, max: -1, percentage: 60 }
];

const ResellerManagement = () => {
  const { user, startImpersonation } = useAuth();
  const isMaster = user?.role === 'master_admin';
  const [activeTab, setActiveTab] = useState('resellers');
  
  // Resellers state
  const [resellers, setResellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Applications state
  const [applications, setApplications] = useState([]);
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [applicationFilter, setApplicationFilter] = useState('all');
  const [pendingCount, setPendingCount] = useState(0);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedReseller, setSelectedReseller] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  
  // Approval form state
  const [approvalData, setApprovalData] = useState({
    revenue_tiers: DEFAULT_REVENUE_TIERS,
    commission_rate: 50,
    admin_notes: ''
  });
  
  // Rejection form state
  const [rejectionReason, setRejectionReason] = useState('');
  
  // Create form state
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    owner_email: '',
    owner_password: '',
    owner_name: '',
    company_name: '',
    commission_rate: 50
  });

  // Edit form state
  const [editFormData, setEditFormData] = useState({
    name: '',
    commission_rate: 50,
    revenue_tiers: DEFAULT_REVENUE_TIERS,
    branding: {
      company_name: '',
      logo_url: '',
      primary_color: '#4F46E5',
      secondary_color: '#7C3AED',
      support_email: ''
    }
  });

  const fetchResellers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, per_page: 10 });
      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const res = await api.get(`/resellers?${params}`);
      setResellers(res.data.resellers);
      setTotalPages(res.data.total_pages);
      setTotal(res.data.total);
    } catch (error) {
      toast.error('Failed to load resellers');
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, statusFilter]);

  const fetchApplications = useCallback(async () => {
    try {
      setLoadingApplications(true);
      const params = applicationFilter !== 'all' ? `?status=${applicationFilter}` : '';
      const res = await api.get(`/resellers/applications${params}`);
      setApplications(res.data.applications);
      setPendingCount(res.data.pending_count);
    } catch (error) {
      toast.error('Failed to load applications');
    } finally {
      setLoadingApplications(false);
    }
  }, [applicationFilter]);

  useEffect(() => {
    if (activeTab === 'resellers') {
      fetchResellers();
    } else {
      fetchApplications();
    }
  }, [activeTab, fetchResellers, fetchApplications]);

  const handleCreateReseller = async () => {
    if (!formData.name || !formData.slug || !formData.owner_email || !formData.owner_password || !formData.owner_name) {
      toast.error('Please fill all required fields');
      return;
    }
    
    try {
      setCreating(true);
      await api.post('/resellers', formData);
      toast.success('Reseller created successfully');
      setShowCreateModal(false);
      setFormData({
        name: '',
        slug: '',
        owner_email: '',
        owner_password: '',
        owner_name: '',
        company_name: '',
        commission_rate: 50
      });
      fetchResellers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create reseller');
    } finally {
      setCreating(false);
    }
  };

  const handleSuspendReseller = async (resellerId) => {
    try {
      await api.post(`/resellers/${resellerId}/suspend`);
      toast.success('Reseller suspended');
      fetchResellers();
    } catch (error) {
      toast.error('Failed to suspend reseller');
    }
  };

  const handleActivateReseller = async (resellerId) => {
    try {
      await api.post(`/resellers/${resellerId}/activate`);
      toast.success('Reseller activated');
      fetchResellers();
    } catch (error) {
      toast.error('Failed to activate reseller');
    }
  };

  const handleViewDetails = async (reseller) => {
    try {
      const res = await api.get(`/resellers/${reseller.reseller_id}`);
      setSelectedReseller(res.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Failed to load reseller details');
    }
  };

  const handleOpenEdit = (reseller) => {
    setEditFormData({
      name: reseller.name || '',
      commission_rate: reseller.plan?.commission_rate ?? reseller.commission_rate ?? 50,
      revenue_tiers: reseller.revenue_tiers || DEFAULT_REVENUE_TIERS,
      branding: {
        company_name: reseller.branding?.company_name || '',
        logo_url: reseller.branding?.logo_url || '',
        primary_color: reseller.branding?.primary_color || '#4F46E5',
        secondary_color: reseller.branding?.secondary_color || '#7C3AED',
        support_email: reseller.branding?.support_email || ''
      }
    });
    setSelectedReseller(reseller);
    setShowEditModal(true);
    setShowDetailModal(false);
  };

  const handleImpersonateReseller = async (reseller) => {
    try {
      const res = await api.post(`/resellers/${reseller.reseller_id}/impersonate`);
      startImpersonation(res.data.access_token, res.data.user);
      toast.success(`Now impersonating ${res.data.user.email}`);
      window.location.href = '/reseller/dashboard';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to impersonate reseller');
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedReseller) return;
    
    setSaving(true);
    try {
      const payload = {
        name: editFormData.name,
        branding: editFormData.branding
      };
      // Commission rate + bonus tiers are Master-Admin-only (backend also enforces).
      if (isMaster) {
        payload.commission_rate = editFormData.commission_rate;
        payload.revenue_tiers = editFormData.revenue_tiers;
      }
      await api.put(`/resellers/${selectedReseller.reseller_id}`, payload);
      toast.success('Reseller updated successfully');
      setShowEditModal(false);
      fetchResellers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update reseller');
    } finally {
      setSaving(false);
    }
  };

  const handleViewApplication = (application) => {
    setSelectedApplication(application);
    setShowApplicationModal(true);
  };

  const handleOpenApprove = (application) => {
    setSelectedApplication(application);
    setApprovalData({
      revenue_tiers: DEFAULT_REVENUE_TIERS,
      commission_rate: 50,
      admin_notes: ''
    });
    setShowApproveModal(true);
    setShowApplicationModal(false);
  };

  const handleOpenReject = (application) => {
    setSelectedApplication(application);
    setRejectionReason('');
    setShowRejectModal(true);
    setShowApplicationModal(false);
  };

  const handleApproveApplication = async () => {
    if (!selectedApplication) return;
    
    setApproving(true);
    try {
      const res = await api.post(`/resellers/applications/${selectedApplication.application_id}/approve`, approvalData);
      const data = res.data;
      if (data.temp_password) {
        toast.success(
          `Approved! Temp login: ${selectedApplication.email} / ${data.temp_password}`,
          { duration: 15000 }
        );
      } else {
        toast.success(`Application approved! ${selectedApplication.company_name} is now a reseller.`);
      }
      setShowApproveModal(false);
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve application');
    } finally {
      setApproving(false);
    }
  };

  const handleRejectApplication = async () => {
    if (!selectedApplication) return;
    
    setRejecting(true);
    try {
      await api.post(`/resellers/applications/${selectedApplication.application_id}/reject`, {
        reason: rejectionReason || 'Application did not meet our requirements'
      });
      toast.success('Application rejected');
      setShowRejectModal(false);
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject application');
    } finally {
      setRejecting(false);
    }
  };

  const updateRevenueTier = (index, field, value) => {
    const newTiers = [...approvalData.revenue_tiers];
    newTiers[index] = { ...newTiers[index], [field]: parseInt(value) || 0 };
    setApprovalData({ ...approvalData, revenue_tiers: newTiers });
  };

  const updateEditTier = (index, field, value) => {
    const newTiers = [...(editFormData.revenue_tiers || DEFAULT_REVENUE_TIERS)];
    newTiers[index] = { ...newTiers[index], [field]: parseInt(value) || 0 };
    setEditFormData({ ...editFormData, revenue_tiers: newTiers });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-700">Active</Badge>;
      case 'suspended':
        return <Badge className="bg-red-100 text-red-700">Suspended</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-700">Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700">Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/[\s]+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 min-w-0 p-8">
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Reseller Management
            </h1>
            <p className="text-slate-500 mt-1">Manage your reseller partners and applications</p>
          </div>
          <Button onClick={() => setShowCreateModal(true)} className="gap-2">
            <Plus size={18} />
            Add Reseller
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border">
            <TabsTrigger value="resellers" className="gap-2">
              <Building2 size={16} />
              Resellers ({total})
            </TabsTrigger>
            <TabsTrigger value="applications" className="gap-2 relative">
              <FileText size={16} />
              Applications
              {pendingCount > 0 && (
                <span className="ml-1 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[20px]">
                  {pendingCount}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Resellers Tab */}
          <TabsContent value="resellers" className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <Building2 className="text-indigo-600" size={24} />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Total Resellers</p>
                      <p className="text-2xl font-bold text-slate-900">{total}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <Check className="text-green-600" size={24} />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Active</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {resellers.filter(r => r.status === 'active').length}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Users className="text-purple-600" size={24} />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Total Customers</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {resellers.reduce((sum, r) => sum + (r.customer_count || 0), 0)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="text-amber-600" size={24} />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Avg. Commission</p>
                      <p className="text-2xl font-bold text-slate-900">35%</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Filters */}
            <div className="flex gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <Input
                  placeholder="Search resellers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <Filter size={16} className="mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Resellers Table */}
            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="animate-spin text-slate-400" size={32} />
                  </div>
                ) : resellers.length === 0 ? (
                  <div className="text-center py-12">
                    <Building2 className="mx-auto text-slate-300 mb-4" size={48} />
                    <h3 className="text-lg font-medium text-slate-600">No resellers yet</h3>
                    <p className="text-slate-400 mt-1">Create your first reseller to get started</p>
                    <Button onClick={() => setShowCreateModal(true)} className="mt-4 gap-2">
                      <Plus size={18} />
                      Add Reseller
                    </Button>
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Reseller</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Status</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Customers</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Commission</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Created</th>
                        <th className="text-right py-3 px-4 font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {resellers.map((reseller) => (
                        <tr key={reseller.reseller_id} className="hover:bg-slate-50">
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-3">
                              <div 
                                className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                                style={{ backgroundColor: reseller.branding?.primary_color || '#4F46E5' }}
                              >
                                {reseller.name.charAt(0)}
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">{reseller.name}</p>
                                <p className="text-sm text-slate-500">{reseller.slug}.nexuscms.ai</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            {getStatusBadge(reseller.status)}
                          </td>
                          <td className="py-4 px-4">
                            <span className="font-medium">{reseller.customer_count || 0}</span>
                            <span className="text-slate-400"> / {reseller.plan?.max_customers || 50}</span>
                          </td>
                          <td className="py-4 px-4">
                            <span className="font-medium">{reseller.plan?.commission_rate || 50}%</span>
                          </td>
                          <td className="py-4 px-4 text-slate-500">
                            {new Date(reseller.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-4 px-4 text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreVertical size={16} />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleViewDetails(reseller)}>
                                  <Eye size={16} className="mr-2" />
                                  View Details
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleOpenEdit(reseller)}>
                                  <Edit size={16} className="mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleImpersonateReseller(reseller)} data-testid={`impersonate-reseller-${reseller.reseller_id}`}>
                                  <LogIn size={16} className="mr-2" />
                                  Impersonate
                                </DropdownMenuItem>
                                {reseller.status === 'active' ? (
                                  <DropdownMenuItem 
                                    onClick={() => handleSuspendReseller(reseller.reseller_id)}
                                    className="text-red-600"
                                  >
                                    <Pause size={16} className="mr-2" />
                                    Suspend
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem 
                                    onClick={() => handleActivateReseller(reseller.reseller_id)}
                                    className="text-green-600"
                                  >
                                    <Play size={16} className="mr-2" />
                                    Activate
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-between items-center">
                <p className="text-sm text-slate-500">
                  Showing {resellers.length} of {total} resellers
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft size={16} />
                  </Button>
                  <span className="px-3 py-1 text-sm">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <ChevronRight size={16} />
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Applications Tab */}
          <TabsContent value="applications" className="space-y-6">
            {/* Application Stats */}
            <div className="grid grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                      <Clock className="text-yellow-600" size={24} />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Pending Review</p>
                      <p className="text-2xl font-bold text-slate-900">{pendingCount}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <CheckCircle className="text-green-600" size={24} />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Approved</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {applications.filter(a => a.status === 'approved').length}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                      <XCircle className="text-red-600" size={24} />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Rejected</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {applications.filter(a => a.status === 'rejected').length}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Filter */}
            <div className="flex gap-4">
              <Select value={applicationFilter} onValueChange={setApplicationFilter}>
                <SelectTrigger className="w-48">
                  <Filter size={16} className="mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Applications</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Applications List */}
            <Card>
              <CardContent className="p-0">
                {loadingApplications ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="animate-spin text-slate-400" size={32} />
                  </div>
                ) : applications.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="mx-auto text-slate-300 mb-4" size={48} />
                    <h3 className="text-lg font-medium text-slate-600">No applications yet</h3>
                    <p className="text-slate-400 mt-1">Applications will appear here when users apply to become resellers</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {applications.map((application) => (
                      <div 
                        key={application.application_id} 
                        className="p-4 hover:bg-slate-50 cursor-pointer"
                        onClick={() => handleViewApplication(application)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                              <Briefcase className="text-indigo-600" size={24} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-slate-900">{application.company_name}</h3>
                                {getStatusBadge(application.status)}
                              </div>
                              <p className="text-sm text-slate-500">{application.contact_name}</p>
                              <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                                <span className="flex items-center gap-1">
                                  <Mail size={14} />
                                  {application.email}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Globe size={14} />
                                  {application.country}
                                </span>
                                {application.phone && (
                                  <span className="flex items-center gap-1">
                                    <Phone size={14} />
                                    {application.phone}
                                  </span>
                                )}
                              </div>
                              <div className="mt-2">
                                <span className="text-xs bg-slate-100 px-2 py-1 rounded">
                                  Expected clients: {application.expected_clients}
                                </span>
                                <span className="text-xs bg-slate-100 px-2 py-1 rounded ml-2">
                                  Target: {application.target_market}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-slate-400">
                              Submitted {new Date(application.submitted_at).toLocaleDateString()}
                            </p>
                            {application.status === 'pending' && (
                              <div className="flex gap-2 mt-2">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-green-600 border-green-200 hover:bg-green-50"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenApprove(application);
                                  }}
                                >
                                  <CheckCircle size={14} className="mr-1" />
                                  Approve
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenReject(application);
                                  }}
                                >
                                  <XCircle size={14} className="mr-1" />
                                  Reject
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Create Reseller Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="text-indigo-600" size={24} />
                Create New Reseller
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Reseller Name *</Label>
                  <Input
                    placeholder="Digital Agency Pro"
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        name: e.target.value,
                        slug: generateSlug(e.target.value)
                      });
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Slug *</Label>
                  <Input
                    placeholder="digital-agency-pro"
                    value={formData.slug}
                    onChange={(e) => setFormData({...formData, slug: e.target.value})}
                  />
                  <p className="text-xs text-slate-500">{formData.slug || 'slug'}.nexuscms.ai</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Display Name (White-Label)</Label>
                <Input
                  placeholder="Agency Pro CMS"
                  value={formData.company_name}
                  onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                />
                <p className="text-xs text-slate-500">Shown instead of &quot;Nexus CMS&quot; in their portal</p>
              </div>

              <div className="border-t pt-4">
                <p className="font-medium text-slate-700 mb-3">Owner Account</p>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Owner Name *</Label>
                    <Input
                      placeholder="John Smith"
                      value={formData.owner_name}
                      onChange={(e) => setFormData({...formData, owner_name: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Owner Email *</Label>
                    <Input
                      type="email"
                      placeholder="john@agency.com"
                      value={formData.owner_email}
                      onChange={(e) => setFormData({...formData, owner_email: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Password *</Label>
                    <Input
                      type="password"
                      placeholder="********"
                      value={formData.owner_password}
                      onChange={(e) => setFormData({...formData, owner_password: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="space-y-2">
                  <Label>Reseller Commission Rate</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={formData.commission_rate}
                      onChange={(e) => setFormData({...formData, commission_rate: parseFloat(e.target.value)})}
                      className="w-24"
                    />
                    <span className="text-slate-500">%</span>
                  </div>
                  <p className="text-xs text-slate-500">
                    Reseller keeps {formData.commission_rate}%, platform keeps {100 - formData.commission_rate}%
                  </p>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateReseller} disabled={creating}>
                {creating ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={16} />
                    Creating...
                  </>
                ) : (
                  'Create Reseller'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reseller Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                {selectedReseller && (
                  <>
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-xl"
                      style={{ backgroundColor: selectedReseller.branding?.primary_color || '#4F46E5' }}
                    >
                      {selectedReseller.name?.charAt(0)}
                    </div>
                    <div>
                      <span>{selectedReseller.name}</span>
                      {getStatusBadge(selectedReseller.status)}
                    </div>
                  </>
                )}
              </DialogTitle>
            </DialogHeader>
            
            {selectedReseller && (
              <div className="space-y-6 py-4">
                {/* Stats Grid */}
                <div className="grid grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-sm text-slate-500">Customers</p>
                      <p className="text-2xl font-bold">{selectedReseller.customer_count}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-sm text-slate-500">Commission Rate</p>
                      <p className="text-2xl font-bold">{selectedReseller.plan?.commission_rate}%</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-sm text-slate-500">Pending Payout</p>
                      <p className="text-2xl font-bold">${selectedReseller.billing?.pending_balance?.toFixed(2) || '0.00'}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Owner Info */}
                {selectedReseller.owner && (
                  <div>
                    <p className="font-medium text-slate-700 mb-2">Owner</p>
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <p className="font-medium">{selectedReseller.owner.full_name}</p>
                      <p className="text-sm text-slate-500">{selectedReseller.owner.email}</p>
                    </div>
                  </div>
                )}

                {/* Recent Customers */}
                {selectedReseller.recent_customers?.length > 0 && (
                  <div>
                    <p className="font-medium text-slate-700 mb-2">Recent Customers</p>
                    <div className="space-y-2">
                      {selectedReseller.recent_customers.map((customer) => (
                        <div key={customer.tenant_id} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium">{customer.name}</p>
                            <p className="text-sm text-slate-500">{customer.plan_level} plan</p>
                          </div>
                          <Badge variant="outline">{customer.subscription_status}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Branding */}
                <div>
                  <p className="font-medium text-slate-700 mb-2">Branding</p>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-6 h-6 rounded border"
                        style={{ backgroundColor: selectedReseller.branding?.primary_color }}
                      />
                      <span className="text-sm">Primary</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-6 h-6 rounded border"
                        style={{ backgroundColor: selectedReseller.branding?.secondary_color }}
                      />
                      <span className="text-sm">Secondary</span>
                    </div>
                    <div className="text-sm text-slate-500">
                      Display: {selectedReseller.branding?.company_name || 'Nexus CMS'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                Close
              </Button>
              <Button onClick={() => handleOpenEdit(selectedReseller)}>
                <Edit size={16} className="mr-2" />
                Edit Reseller
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Reseller Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="text-blue-600" size={20} />
                Edit Reseller
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Reseller Name *</Label>
                <Input 
                  placeholder="Agency name"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Base Commission Rate (%)</Label>
                <Input 
                  type="number"
                  min="0"
                  max="100"
                  value={editFormData.commission_rate}
                  disabled={!isMaster}
                  onChange={(e) => setEditFormData({ ...editFormData, commission_rate: parseFloat(e.target.value) || 0 })}
                  data-testid="edit-commission-rate"
                />
                <p className="text-xs text-slate-500">Reseller keeps {editFormData.commission_rate || 0}%, platform keeps {100 - (editFormData.commission_rate || 0)}%. Applies when no revenue tier matches.</p>
              </div>

              {/* Bonus commission tiers — editable by Master Admin only.
                  Super Admin sees them read-only. */}
              <div className="border-t pt-4 mt-2">
                <div className="flex items-center justify-between mb-1">
                  <Label className="font-medium">Bonus Commission Tiers</Label>
                  {!isMaster && <span className="text-xs text-amber-600" data-testid="tiers-readonly-badge">Read-only (Master Admin edits)</span>}
                </div>
                <p className="text-xs text-slate-500 mb-3">Reseller's share % increases with their monthly recurring revenue (MRR).</p>
                <div className="space-y-2" data-testid="edit-revenue-tiers">
                  {(editFormData.revenue_tiers || DEFAULT_REVENUE_TIERS).map((tier, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-4">
                        <Input type="number" value={tier.min} disabled={!isMaster}
                          onChange={(e) => updateEditTier(index, 'min', e.target.value)}
                          className="h-8 text-xs" placeholder="Min $" />
                      </div>
                      <div className="col-span-4">
                        <Input type="number" value={tier.max === -1 ? '' : tier.max} disabled={!isMaster}
                          onChange={(e) => updateEditTier(index, 'max', e.target.value || -1)}
                          className="h-8 text-xs" placeholder="Max $ (∞)" />
                      </div>
                      <div className="col-span-4 flex items-center gap-1">
                        <Input type="number" value={tier.percentage} disabled={!isMaster}
                          onChange={(e) => updateEditTier(index, 'percentage', e.target.value)}
                          className="h-8 text-xs" />
                        <span className="text-xs text-slate-500">%</span>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-slate-400 mt-2">MRR $0–5k, $5k–20k, $20k+ map to each tier. Leave Max blank for the top (unlimited) tier.</p>
              </div>

              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-3">Branding Settings</h4>
                
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>Display Name</Label>
                    <Input 
                      placeholder="Agency CMS"
                      value={editFormData.branding.company_name}
                      onChange={(e) => setEditFormData({ 
                        ...editFormData, 
                        branding: { ...editFormData.branding, company_name: e.target.value }
                      })}
                    />
                    <p className="text-xs text-slate-500">Shown to customers instead of Nexus CMS</p>
                  </div>

                  <div className="space-y-2">
                    <Label>Logo URL</Label>
                    <Input 
                      placeholder="https://example.com/logo.png"
                      value={editFormData.branding.logo_url}
                      onChange={(e) => setEditFormData({ 
                        ...editFormData, 
                        branding: { ...editFormData.branding, logo_url: e.target.value }
                      })}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Primary Color</Label>
                      <div className="flex gap-2">
                        <Input 
                          value={editFormData.branding.primary_color}
                          onChange={(e) => setEditFormData({ 
                            ...editFormData, 
                            branding: { ...editFormData.branding, primary_color: e.target.value }
                          })}
                        />
                        <input
                          type="color"
                          value={editFormData.branding.primary_color}
                          onChange={(e) => setEditFormData({ 
                            ...editFormData, 
                            branding: { ...editFormData.branding, primary_color: e.target.value }
                          })}
                          className="w-10 h-10 rounded border cursor-pointer"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Secondary Color</Label>
                      <div className="flex gap-2">
                        <Input 
                          value={editFormData.branding.secondary_color}
                          onChange={(e) => setEditFormData({ 
                            ...editFormData, 
                            branding: { ...editFormData.branding, secondary_color: e.target.value }
                          })}
                        />
                        <input
                          type="color"
                          value={editFormData.branding.secondary_color}
                          onChange={(e) => setEditFormData({ 
                            ...editFormData, 
                            branding: { ...editFormData.branding, secondary_color: e.target.value }
                          })}
                          className="w-10 h-10 rounded border cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Support Email</Label>
                    <Input 
                      type="email"
                      placeholder="support@agency.com"
                      value={editFormData.branding.support_email}
                      onChange={(e) => setEditFormData({ 
                        ...editFormData, 
                        branding: { ...editFormData.branding, support_email: e.target.value }
                      })}
                    />
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditModal(false)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={handleSaveEdit} disabled={saving || !editFormData.name.trim()}>
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Application Detail Modal */}
        <Dialog open={showApplicationModal} onOpenChange={setShowApplicationModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="text-indigo-600" size={24} />
                Application Details
              </DialogTitle>
            </DialogHeader>
            
            {selectedApplication && (
              <div className="space-y-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold">{selectedApplication.company_name}</h2>
                    <p className="text-slate-500">{selectedApplication.contact_name}</p>
                  </div>
                  {getStatusBadge(selectedApplication.status)}
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-slate-500">Email</Label>
                      <p className="font-medium">{selectedApplication.email}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Phone</Label>
                      <p className="font-medium">{selectedApplication.phone || 'Not provided'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Website</Label>
                      <p className="font-medium">{selectedApplication.website || 'Not provided'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Country</Label>
                      <p className="font-medium">{selectedApplication.country}</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-slate-500">Expected Clients</Label>
                      <p className="font-medium">{selectedApplication.expected_clients}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Target Market</Label>
                      <p className="font-medium">{selectedApplication.target_market}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Experience</Label>
                      <p className="font-medium">{selectedApplication.experience || 'Not provided'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Submitted</Label>
                      <p className="font-medium">
                        {new Date(selectedApplication.submitted_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>

                {selectedApplication.why_partner && (
                  <div>
                    <Label className="text-slate-500">Why they want to partner</Label>
                    <p className="mt-1 p-3 bg-slate-50 rounded-lg">{selectedApplication.why_partner}</p>
                  </div>
                )}

                {selectedApplication.status === 'rejected' && selectedApplication.rejection_reason && (
                  <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                    <Label className="text-red-700">Rejection Reason</Label>
                    <p className="text-red-600">{selectedApplication.rejection_reason}</p>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowApplicationModal(false)}>
                Close
              </Button>
              {selectedApplication?.status === 'pending' && (
                <>
                  <Button 
                    variant="outline" 
                    className="text-red-600 border-red-200"
                    onClick={() => handleOpenReject(selectedApplication)}
                  >
                    <XCircle size={16} className="mr-2" />
                    Reject
                  </Button>
                  <Button 
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => handleOpenApprove(selectedApplication)}
                  >
                    <CheckCircle size={16} className="mr-2" />
                    Approve
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Approve Application Modal */}
        <Dialog open={showApproveModal} onOpenChange={setShowApproveModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle className="text-green-600" size={24} />
                Approve Application
              </DialogTitle>
              <DialogDescription>
                Configure the revenue share and settings for {selectedApplication?.company_name}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Revenue Share Tiers */}
              <div>
                <Label className="text-base font-semibold">Revenue Share Tiers</Label>
                <p className="text-sm text-slate-500 mb-3">
                  Set tiered revenue share based on monthly revenue
                </p>
                <div className="space-y-3">
                  {approvalData.revenue_tiers.map((tier, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <div className="flex-1">
                        <Label className="text-xs text-slate-500">From ($)</Label>
                        <Input
                          type="number"
                          value={tier.min}
                          onChange={(e) => updateRevenueTier(index, 'min', e.target.value)}
                          className="h-8"
                        />
                      </div>
                      <div className="flex-1">
                        <Label className="text-xs text-slate-500">To ($)</Label>
                        <Input
                          type="number"
                          value={tier.max === -1 ? '' : tier.max}
                          placeholder="Unlimited"
                          onChange={(e) => updateRevenueTier(index, 'max', e.target.value || -1)}
                          className="h-8"
                        />
                      </div>
                      <div className="w-24">
                        <Label className="text-xs text-slate-500">Share (%)</Label>
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            value={tier.percentage}
                            onChange={(e) => updateRevenueTier(index, 'percentage', e.target.value)}
                            className="h-8"
                          />
                          <Percent size={14} className="text-slate-400" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Higher tiers = higher revenue share for successful resellers
                </p>
              </div>

              {/* Legacy Commission Rate */}
              <div>
                <Label>Base Commission Rate (Legacy)</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={approvalData.commission_rate}
                    onChange={(e) => setApprovalData({...approvalData, commission_rate: parseInt(e.target.value) || 0})}
                    className="w-24"
                  />
                  <span className="text-slate-500">%</span>
                </div>
                <p className="text-xs text-slate-500">Fallback rate if tier calculation fails</p>
              </div>

              {/* Admin Notes */}
              <div>
                <Label>Admin Notes (Internal)</Label>
                <Textarea
                  placeholder="Notes about this reseller..."
                  value={approvalData.admin_notes}
                  onChange={(e) => setApprovalData({...approvalData, admin_notes: e.target.value})}
                  rows={3}
                />
              </div>

              {/* Summary */}
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-medium text-green-800 mb-2">What happens on approval:</h4>
                <ul className="text-sm text-green-700 space-y-1">
                  <li>- A new reseller account will be created</li>
                  <li>- The applicant will receive login credentials</li>
                  <li>- They can start adding customers immediately</li>
                  <li>- Revenue share: {approvalData.revenue_tiers[0]?.percentage}% starting tier</li>
                </ul>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowApproveModal(false)} disabled={approving}>
                Cancel
              </Button>
              <Button 
                className="bg-green-600 hover:bg-green-700"
                onClick={handleApproveApplication}
                disabled={approving}
              >
                {approving ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={16} />
                    Approving...
                  </>
                ) : (
                  <>
                    <CheckCircle size={16} className="mr-2" />
                    Approve Application
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject Application Modal */}
        <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <XCircle className="text-red-600" size={24} />
                Reject Application
              </DialogTitle>
              <DialogDescription>
                Provide a reason for rejecting {selectedApplication?.company_name}&apos;s application
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label>Rejection Reason</Label>
                <Textarea
                  placeholder="Enter the reason for rejection..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={4}
                />
                <p className="text-xs text-slate-500 mt-1">
                  This will be sent to the applicant
                </p>
              </div>

              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <div className="flex items-start gap-2">
                  <AlertCircle className="text-amber-600 mt-0.5" size={16} />
                  <div>
                    <h4 className="font-medium text-amber-800">This action cannot be undone</h4>
                    <p className="text-sm text-amber-700">
                      The applicant will be notified of the rejection.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRejectModal(false)} disabled={rejecting}>
                Cancel
              </Button>
              <Button 
                variant="destructive"
                onClick={handleRejectApplication}
                disabled={rejecting}
              >
                {rejecting ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={16} />
                    Rejecting...
                  </>
                ) : (
                  <>
                    <XCircle size={16} className="mr-2" />
                    Reject Application
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ResellerManagement;
